import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useMarkNoShow, useCancelSession } from 'hooks/useSessions';
import type { Session } from 'types/models';

type ActionType = 'no-show' | 'cancel';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  actionType: ActionType;
  session: Session;
  patientId: string;
  episodeId: string;
  planId: string;
}

const CONFIG: Record<ActionType, { title: string; description: string; confirmLabel: string; colorScheme: string }> = {
  'no-show': {
    title: 'Registrar Inasistencia',
    description: 'El paciente no asistió a esta sesión. Se registrará como inasistencia en el historial.',
    confirmLabel: 'Marcar Inasistencia',
    colorScheme: 'red',
  },
  'cancel': {
    title: 'Cancelar Sesión',
    description: 'La sesión será cancelada y se liberará el espacio en la agenda.',
    confirmLabel: 'Cancelar Sesión',
    colorScheme: 'gray',
  },
};

export default function SessionActionModal({
  isOpen, onClose, actionType, session, patientId, episodeId, planId,
}: Props) {
  const [motivo, setMotivo] = useState('');

  const { mutate: markNoShow, isPending: isPendingNoShow } = useMarkNoShow();
  const { mutate: cancelSession, isPending: isPendingCancel } = useCancelSession();
  const isPending = isPendingNoShow || isPendingCancel;

  const labelColor  = useColorModeValue('gray.600', 'gray.300');
  const descColor   = useColorModeValue('gray.500', 'gray.400');

  const cfg = CONFIG[actionType];

  const handleConfirm = () => {
    const payload = { motivo: motivo.trim() || undefined };
    const args = { patientId, episodeId, planId, sessionId: session.id, payload };

    if (actionType === 'no-show') {
      markNoShow(args, { onSuccess: onClose });
    } else {
      cancelSession(args, { onSuccess: onClose });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="md" fontWeight="700" pb="4px">
          {cfg.title}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pt="4px">
          <Text fontSize="sm" color={descColor} mb="16px">
            {cfg.description}
          </Text>

          <FormControl>
            <FormLabel fontSize="xs" fontWeight="600" color={labelColor} textTransform="uppercase" letterSpacing="0.06em">
              Motivo (opcional)
            </FormLabel>
            <Textarea
              placeholder="Ej: Paciente avisó con anticipación / Emergencia personal..."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={500}
              rows={3}
              fontSize="sm"
              borderRadius="10px"
              resize="none"
            />
          </FormControl>
        </ModalBody>

        <ModalFooter gap="8px">
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isPending}>
            Volver
          </Button>
          <Button
            colorScheme={cfg.colorScheme}
            size="sm"
            borderRadius="8px"
            onClick={handleConfirm}
            isLoading={isPending}
            loadingText="Guardando..."
          >
            {cfg.confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
